import type { EntityHierarchy, EntityTreeNode } from '@/types/order.types';
import type { EntityScopeState } from '@/slice/entityScopeSlice';

import { getCurrentContext } from './current-context';
import { hasWebhookEndpoints } from './entity-doc';
import { getEntityScope } from './entity-scope-read';

/**
 * Map the backend `EntityHierarchy` shape into the `EntityTreeNode` shape
 * the entity-scope slice + `tree-select` primitive consume. Preserves
 * `customerEntityId` and `integrations` so downstream readers
 * (`hasCustomWebhookEndpoints`, entity-dropdown display labels) see the
 * full source data. Pinned by a unit test in `entity-tree.test.ts` because
 * a silent field drop here is invisible to consumer-side tests that seed
 * the scope directly.
 */
export const convertToTreeNode = (entity: EntityHierarchy): EntityTreeNode => ({
  value: entity.entityId,
  label: entity.entityName,
  customerEntityId: entity.customerEntityId,
  integrations: entity.integrations,
  hasWatermark: !!entity.watermarkImage?.trim(),
  children: entity.children ? entity.children.map(convertToTreeNode) : [],
});

type EntitySelectOption = {
  value: string;
  label: string;
  customerEntityId?: string;
};

export type EntityDropdownConfig = {
  options: EntitySelectOption[];
  isDisabled: boolean;
  isRequired: boolean;
  defaultEntityName: string;
  defaultEntityUUID: string;
};

const flattenEntityTree = (node: EntityTreeNode): EntitySelectOption[] => {
  const result: EntitySelectOption[] = [
    {
      value: node.value,
      label: node.label,
      customerEntityId: node.customerEntityId
    }
  ];
  if (node.children && node.children.length > 0) {
    for (const child of node.children) {
      result.push(...flattenEntityTree(child));
    }
  }
  return result;
};

/** The node for `targetId` in `node`'s tree, or null. The one tree walker (DV-3421 reuses it). */
export const findEntityNode = (
  node: EntityTreeNode,
  targetId: string,
): EntityTreeNode | null => {
  if (node.value === targetId) return node;
  for (const child of node.children || []) {
    const found = findEntityNode(child, targetId);
    if (found) return found;
  }
  return null;
};

/**
 * Can this tab show, from what it holds, that `entityId` is inside the tree
 * of the token whose scope root is `root`? DV-3421 — the one local proof,
 * shared by the switch (`reachableWithoutAsking`) and the stamp check for a
 * scope persisted before the root stamp (review of 24a2f326, item 9).
 *
 * <ul>
 *   <li>`entityId` IS the root.
 *   <li>The cached tree contains it, and that tree is the root's — either its
 *       top node is the root, or it was written under this root
 *       (`rootAtWrite`): a tree is the subtree of a selection that was inside
 *       the token's reach when written, and with the root unchanged still is.
 * </ul>
 *
 * <p>Deliberately NOT `parentEntityId === root`, which the stamp check used to
 * accept: the anchor is whatever the switching surface passed, not something
 * the tab has seen in a tree under this root. The header tree is keyed by that
 * anchor, so every case it settled is settled by the tree rule anyway.
 */
export const inTokenTree = (
  scope: Pick<EntityScopeState, 'entityTree' | 'rootAtWrite'>,
  root: string,
  entityId: string,
): boolean =>
  entityId === root ||
  (!!scope.entityTree &&
    (scope.entityTree.value === root || scope.rootAtWrite === root) &&
    findEntityNode(scope.entityTree, entityId) !== null);

export const getEntityDropdownConfig = (): EntityDropdownConfig => {
  const fallbackName = getCurrentContext();
  const fallbackUUID = getEntityScope().entityId ?? '';
  const disabledResult: EntityDropdownConfig = {
    options: [],
    isDisabled: true,
    isRequired: false,
    defaultEntityName: fallbackName,
    defaultEntityUUID: fallbackUUID
  };

  const { entityTree: tree, entityId: selectedEntityId } = getEntityScope();
  if (!tree || !selectedEntityId) return disabledResult;

  const currentNode = findEntityNode(tree, selectedEntityId);
  if (!currentNode) return disabledResult;

  const hasChildren = currentNode.children && currentNode.children.length > 0;
  if (!hasChildren) {
    return {
      ...disabledResult,
      defaultEntityName: currentNode.label,
      defaultEntityUUID: currentNode.value
    };
  }

  // Show self + all descendants (flattened)
  return {
    options: flattenEntityTree(currentNode),
    isDisabled: false,
    isRequired: true,
    defaultEntityName: '',
    defaultEntityUUID: ''
  };
};

/**
 * Whether the SELECTED entity has any custom webhook endpoints configured —
 * the /home Send gate. The internal photo workspace asks the same question of
 * the property's own account instead (`useWorkspaceAccount`, DV-2849 PR 2).
 *
 * Surfaces on the Webhooks tab of the Sync modal: when this returns
 * false, the Send Webhook button is disabled with a "No webhook endpoint
 * exists for this account" message so a misleading success toast doesn't fire
 * for an action that would silently no-op downstream.
 */
export const hasCustomWebhookEndpoints = (): boolean => {
  const { entityTree: tree, entityId: selectedEntityId } = getEntityScope();
  if (!tree || !selectedEntityId) return false;
  return hasWebhookEndpoints(findEntityNode(tree, selectedEntityId)?.integrations);
};

export const getEntityOptions = (): EntitySelectOption[] => {
  const { entityTree: tree, entityId: selectedEntityId } = getEntityScope();
  if (!tree || !selectedEntityId) return [];
  const targetNode = findEntityNode(tree, selectedEntityId);
  if (!targetNode) return [];
  return flattenEntityTree(targetNode);
};

/**
 * Dropdown options for the "Entity ID" column in the bulk-import fix-errors
 * table. Prefers the entity's customer-set ID (`customerEntityId`) and falls
 * back to the entity name (`label`) when the customer hasn't set one. Both
 * value and label are the same display string — the column shows whichever
 * the user is most likely to recognise.
 */
export const getEntityIdOptions = (): Array<{ value: string; label: string }> =>
  getEntityOptions().map((opt) => {
    const display = opt.customerEntityId || opt.label;
    return { value: display, label: display };
  });

/**
 * Default entity value used when a bulk-import row has an empty entity.
 * Prefers the selected entity's customerEntityId, then its label, then the
 * current Keycloak context. Used to pre-populate the Entity ID column on
 * imported rows so the user isn't forced to select for every row.
 */
export const getEntityFallbackValue = (): string => {
  const selectedEntityId = getEntityScope().entityId;
  const currentEntityOption = getEntityOptions().find(
    (opt) => opt.value === selectedEntityId,
  );
  return (
    currentEntityOption?.customerEntityId ||
    currentEntityOption?.label ||
    getCurrentContext()
  );
};

// The tree's ROOT, which is not always the same as the scope's
// `parentEntityId` — this answers "what is the top of the tree we are showing",
// which is what child-scoping is relative to.
const getTreeRootEntityId = (): string | null => getEntityScope().entityTree?.value || null;

export const isChildEntityScoped = (): boolean => {
  const selectedEntity = getEntityScope().entityId ?? '';
  const rootEntityId = getTreeRootEntityId();
  return !!rootEntityId && selectedEntity !== rootEntityId;
};

export const shouldShowEntityFilter = (): boolean => {
  const opts = getEntityOptions();
  return opts.length > 1;
};
